// @ts-ignore - allow import with query string
import { appendScriptTags } from './util.js?v=25';

if (!customElements.get('cubitts-speculator')) {
  customElements.define(
    'cubitts-speculator',
    class CubittsSpeculator extends HTMLElement {
      constructor() {
        super();

        this.CLASSES = {
          variantsLoading: 'speculator__variants--is-loading',
        };

        this.products = this.querySelector('.speculator__products');
        this.productList = this.querySelector('.speculator__product-list');
        this.productTitle = this.querySelectorAll('[js-speculator-product-title]');
        this.currentProduct = this.dataset.currentProduct || null;
        this.filtersTrigger = this.querySelector('.speculator__filters-trigger');
        this.filtersCloseButtons = document.querySelectorAll('[js-speculator-filters-close]');
        this.filters = this?.querySelector('.speculator__filters');
        this.cta = this?.querySelector('.speculator__cta');

        /**
         *
         * @type {HTMLElement | null}
         */
        this.pagination = this.querySelector('.speculator__pagination');
        this.variants = this.querySelectorAll('.speculator__variants');

        this.closeTrigger?.addEventListener('click', this.onTriggerClick.bind(this, 'close'));
        this.openTriggers.forEach((openTrigger) => {
          openTrigger?.addEventListener('click', this.onTriggerClick.bind(this, 'open'));
        });
        this.filtersTrigger?.addEventListener('click', this.onFiltersTriggerClick.bind(this));
        this.filtersCloseButtons?.forEach((filtersClose) => {
          filtersClose?.addEventListener('click', this.onFiltersTriggerClick.bind(this));
        });

        this.productCards && this.attachClickEvent(this.productCards);
        this.nextPageUrl && this.fetchProducts(this.nextPageUrl);

        this.elements.changeFrameButton?.addEventListener('click', this.removeActiveProduct.bind(this));

        document.addEventListener('keyup', this.handleKeyup.bind(this));

        document.addEventListener('speculatorFiltersUpdated', () => this.attachClickEvent(this.productCards));

        this.attachColorTriggers(this.colorTriggers);

        this.initFilters();

        this.initPagination();

        this.isOpen = true;

        this.openColorDetails();
      }

      /**
       *
       * @param {KeyboardEvent} event - Keyboard event.
       */
      handleKeyup(event) {
        switch (event.key) {
          case 'Escape':
            this.isOpen = false;
            break;
          default:
            break;
        }
      }

      initFilters() {
        if (!this.dataset.collectionUrl) return;

        const url = new URL(this.dataset.collectionUrl, window.location.href);
        const params = new URLSearchParams(window.location.search);
        params.append('view', 'speculator');

        this.fetchProducts(url.pathname.toString() + `?${params.toString()}`, true);
      }

      initPagination() {
        if (!this.pagination) return;

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && this.pagination?.dataset.nextPageUrl) {
                this.fetchProducts(this.pagination?.dataset.nextPageUrl);
              }
            });
          },
          { rootMargin: '400px 200px 0px 0px' },
        );

        observer.observe(this.pagination);
      }

      /**
       *
       * @param {NodeListOf<HTMLButtonElement>} products - List of products.
       */
      attachClickEvent(products) {
        products?.forEach((product) => {
          product.addEventListener('click', this.handleProductClick.bind(this, product));
        });
      }

      /**
       *
       * @param {NodeListOf<HTMLInputElement>} colorTriggers - List of products.
       */
      attachColorTriggers(colorTriggers) {
        colorTriggers?.forEach((color) => {
          color.addEventListener(
            'click',
            this.handleColorClick.bind(
              this,
              color.dataset.colorSpeculator ?? undefined,
              color.dataset.variantUrl ?? undefined,
            ),
          );
        });
      }

      /**
       *
       * @param {HTMLButtonElement} product - The product element.
       */
      handleProductClick(product) {
        if (!product.dataset.url) return;

        const modelConfig = product.dataset.modelConfig;

        this.fetchProductData(product.dataset.url).then(() => {
          modelConfig && window.Speculator.set3DModelConfig(JSON.parse(modelConfig));
          this.setActiveProduct(product);

          this.updateBuyButton(product?.dataset?.url, product?.dataset?.price);
        });
      }

      /**
       *
       * @param {String|undefined} color - The color to set in the 3D model.
       * @param {String|undefined} url - The url for the color variant
       */
      handleColorClick(color, url) {
        window.Speculator.set3DModelConfig(color);

        this.updateBuyButton(url, undefined);
      }

      /**
       *
       * @param {HTMLButtonElement} product - The product element.
       */
      setActiveProduct(product) {
        this.removeActiveProduct();

        product.setAttribute('aria-pressed', 'true');

        this.productTitle.forEach((productTitle) => {
          if (productTitle && product.dataset.title) {
            productTitle.textContent = product.dataset.title;
          }
        });

        if (!this.isOpen) {
          this.isOpen = true;
        }
      }

      removeActiveProduct() {
        const activeProduct = this.querySelector('.speculator__product-card[aria-pressed="true"]');

        activeProduct?.removeAttribute('aria-pressed');
        this.openColorDetails();
      }

      /**
       * @param {String|undefined} url - The product url.
       * @param {String|undefined} price - The product price
       */
      updateBuyButton(url, price) {
        const priceElement = this.elements.buyButton?.querySelector('.price');

        if (url) {
          const parsedUrl = new URL(url, window.location.origin);
          parsedUrl.searchParams.set('open-modal', 'product-wizard');

          this.elements.buyButton?.setAttribute('href', parsedUrl.toString());
        }

        if (priceElement && price) {
          priceElement.textContent = price.replace('.00', '');
        }
      }

      /**
       *
       * @param {String} href - Url to fetch
       */
      async fetchProductData(href) {
        this.toggleVariantsLoading();
        const url = new URL(href, window.location.href);
        const params = new URLSearchParams(url.search);
        params.append('section_id', 'speculator-variants');

        fetch(url.pathname.toString() + `?${params.toString()}`)
          .then((response) => response.text())
          .then((html) => {
            const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
            const content = parsedHTML.querySelector('.speculator-variants');
            const scriptTags = content?.querySelectorAll('script');

            if (this.colorSelectors.length > 0) {
              this.colorSelectors.forEach((colorSelector) => {
                const isOpen = colorSelector
                  ?.querySelector('.speculator__options-details--colour')
                  ?.hasAttribute('open');
                const optionsDetails = content?.querySelector('.speculator__options-details--colour');
                const optionsContainer = optionsDetails?.closest('.speculator-variants');

                isOpen ? optionsDetails?.setAttribute('open', 'true') : optionsDetails?.removeAttribute('open');

                if (!optionsContainer) return;

                colorSelector.innerHTML = optionsContainer.innerHTML;
              });
            } else {
              this.variantContainers.forEach((variantContainer) => {
                variantContainer.innerHTML = content?.innerHTML ?? '';
              });
            }

            this.attachColorTriggers(this.colorTriggers);
            this.toggleVariantsLoading();
            this.openColorDetails();

            appendScriptTags(scriptTags);
          })
          .catch((e) => {
            console.error(e);
          });
      }

      /**
       * @param {String} url - The product element.
       * @param {Boolean} onlyFilters - Only render the filter
       */
      fetchProducts(url, onlyFilters = false) {
        fetch(url)
          .then((response) => response.text())
          .then((html) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            /**
             * @type {NodeListOf<HTMLButtonElement>}
             */
            const productCards = doc.querySelectorAll('.speculator__product-card');
            const filters = doc.querySelector('.speculator__filters');
            const scriptTags = filters?.querySelectorAll('script');
            /**
             * @type {HTMLElement|null}
             */
            const pagination = doc.querySelector('.speculator__pagination');

            this.attachClickEvent(productCards);

            if (onlyFilters) {
              if (this.filters && filters) {
                this.filters.innerHTML = filters.innerHTML;
              }
            } else {
              productCards.forEach((product) => {
                if (!this.productList || this.currentProduct === product.dataset.productHandle) return;

                this.productList.appendChild(product);
              });

              if (this.pagination && pagination?.dataset.nextPageUrl) {
                this.pagination.setAttribute('data-next-page-url', pagination.dataset.nextPageUrl);
              } else if (this.pagination) {
                this.pagination.remove();
              }

              appendScriptTags(scriptTags);
            }
          })
          .catch((e) => {
            console.error(e);
          });
      }

      /**
       *
       * @param {String} state - State of the speculator
       */
      onTriggerClick(state) {
        state === 'open' ? (this.isOpen = true) : (this.isOpen = false);
      }

      onFiltersTriggerClick() {
        if (this.filtersTrigger?.getAttribute('aria-pressed') === 'true') {
          this.filtersTrigger?.removeAttribute('aria-pressed');
          this.filters?.setAttribute('aria-hidden', 'true');
          this.products?.classList.remove('util-hidden-md');
          this.cta?.removeAttribute('aria-hidden');

          this.filtersCloseButtons?.forEach((filtersClose) => {
            filtersClose?.setAttribute('aria-hidden', 'true');
          });
        } else {
          this.filtersTrigger?.setAttribute('aria-pressed', 'true');
          this.filters?.removeAttribute('aria-hidden');
          this.products?.classList.add('util-hidden-md');
          this.cta?.setAttribute('aria-hidden', 'true');

          this.filtersCloseButtons?.forEach((filtersClose) => {
            filtersClose?.removeAttribute('aria-hidden');
          });
        }
      }

      startSpeculator() {
        if (!this._hasBeenInitialised) {
          window.Speculator.init();

          this._hasBeenInitialised = true;
        } else {
          window.artov5.api.startCamera();
        }
      }

      stopSpeculator() {
        window.artov5.api.stopCamera();
      }

      toggleVariantsLoading() {
        this.variants.forEach((variantsElement) => {
          variantsElement.classList.contains(this.CLASSES.variantsLoading)
            ? variantsElement.classList.remove(this.CLASSES.variantsLoading)
            : variantsElement.classList.add(this.CLASSES.variantsLoading);
        });
      }

      openColorDetails() {
        this.isMobile && this.elements.colorDetails?.setAttribute('open', 'true');
      }

      /**
       * @type {Boolean|undefined}
       */
      get isOpen() {
        return this._isOpen;
      }

      set isOpen(val) {
        const previousVal = this._isOpen;
        this._isOpen = val;

        if (val) {
          this.startSpeculator();
        }

        if (!val && previousVal) {
          this.stopSpeculator();
        }

        this.hidden = !val;
      }

      get closeTrigger() {
        return document.querySelector('[js-speculator-close]');
      }

      get openTriggers() {
        return document.querySelectorAll('[js-speculator-trigger]');
      }

      get api() {
        return window.artov5.api;
      }

      /**
       * @type {NodeListOf<HTMLButtonElement>}
       */
      get productCards() {
        return this.querySelectorAll('.speculator__product-card');
      }

      /**
       * @type {NodeListOf<HTMLInputElement>}
       */
      get colorTriggers() {
        return this.querySelectorAll('[js-speculator-color-trigger]');
      }

      get colorSelectors() {
        return this.querySelectorAll('.speculator__option-selector--colour');
      }

      get variantContainers() {
        return this.querySelectorAll('.speculator__variants');
      }

      get nextPageUrl() {
        return this.pagination?.dataset.nextPageUrl;
      }

      get elements() {
        return {
          buyButton: this.querySelector('[js-speculator-buy-button]'),
          changeFrameButton: this.querySelector('[js-speculator-change-frame]'),
          colorDetails: this.querySelector('.speculator__options-details--colour'),
          /**
           * @type {HTMLElement | null}
           */
          content: document.querySelector('[js-speculator-content]'),
        };
      }

      get isMobile() {
        return window.innerWidth < 992;
      }
    },
  );
}
